import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NotificationModal from '../shared/NotificationModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface UserFolder {
  id: number;
  user_id: number;
  name: string;
  hotkey: string;
  color: string;
  created_at: string;
}

interface FolderManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onFoldersChange: () => void;
}

const FolderManager: React.FC<FolderManagerProps> = ({ isOpen, onClose, onFoldersChange }) => {
  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingFolder, setEditingFolder] = useState<UserFolder | null>(null);
  const [newFolder, setNewFolder] = useState({
    name: '',
    hotkey: '',
    color: '#3B82F6'
  });
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('success');

  useEffect(() => {
    if (isOpen) {
      fetchFolders();
    }
  }, [isOpen]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/folders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFolders(response.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const createFolder = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = newFolder.name.trim();
    if (!trimmedName) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/folders`, {
        ...newFolder,
        name: trimmedName  // Send trimmed name to prevent space issues
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      setNotificationMessage(`Folder "${trimmedName}" created successfully!`);
      setNotificationType('success');
      setShowNotification(true);

      // Reset form
      setNewFolder({ name: '', hotkey: '', color: '#3B82F6' });

      // Refresh folders
      fetchFolders();
      onFoldersChange();

      // Close modal after short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to create folder:', error);
      setNotificationMessage('Failed to create folder. Please try again.');
      setNotificationType('error');
      setShowNotification(true);
    }
  };

  const updateFolder = async (folder: UserFolder) => {
    const trimmedName = folder.name.trim();
    if (!trimmedName) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/folders/${folder.id}`, {
        name: trimmedName,  // Send trimmed name to prevent space issues
        hotkey: folder.hotkey,
        color: folder.color
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setEditingFolder(null);
      fetchFolders();
      onFoldersChange();

      // Show success notification
      setNotificationMessage(`Folder "${trimmedName}" updated successfully!`);
      setNotificationType('success');
      setShowNotification(true);
    } catch (error) {
      console.error('Failed to update folder:', error);
      setNotificationMessage('Failed to update folder. Please try again.');
      setNotificationType('error');
      setShowNotification(true);
    }
  };

  const deleteFolder = async (folderId: number, folderName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${folderName}"? Images will not be deleted.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE}/folders/${folderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 200) {
        fetchFolders();
        onFoldersChange();
      }
    } catch (error) {
      console.error('Failed to delete folder:', error);
      alert('Failed to delete folder. Please try again.');
    }
  };

  const getUsedHotkeys = (excludeId?: number) => {
    return folders
      .filter(f => f.id !== excludeId)
      .map(f => f.hotkey?.toLowerCase())
      .filter(Boolean);
  };

  const suggestHotkey = (excludeId?: number) => {
    const used = getUsedHotkeys(excludeId);
    
    // Try numbers first (1-9)
    for (let i = 1; i <= 9; i++) {
      if (!used.includes(i.toString())) {
        return i.toString();
      }
    }
    
    // Then letters (a-z)
    for (let i = 97; i <= 122; i++) {
      const letter = String.fromCharCode(i);
      if (!used.includes(letter)) {
        return letter;
      }
    }
    
    return '';
  };

  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
    '#F97316', '#6366F1', '#14B8A6', '#F43F5E'
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manage Folders</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Create New Folder */}
          <form onSubmit={createFolder} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Create New Folder</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Folder Name
                  </label>
                  <input
                    type="text"
                    value={newFolder.name}
                    onChange={(e) => setNewFolder({...newFolder, name: e.target.value})}
                    placeholder="e.g., Favorites, Archive"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={50}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hotkey
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={newFolder.hotkey}
                      onChange={(e) => setNewFolder({...newFolder, hotkey: e.target.value.toLowerCase().slice(0, 1)})}
                      placeholder="1-9, a-z"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={1}
                    />
                    <button
                      type="button"
                      onClick={() => setNewFolder({...newFolder, hotkey: suggestHotkey()})}
                      className="px-3 py-2 bg-gray-200 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-300 text-sm"
                      title="Suggest available hotkey"
                    >
                      Auto
                    </button>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="grid grid-cols-6 gap-2 max-w-xs">
                  {colors.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewFolder({...newFolder, color})}
                      className={`w-8 h-8 rounded border-2 ${newFolder.color === color ? 'border-gray-800' : 'border-gray-300'} hover:scale-110 transition-transform`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={!newFolder.name.trim()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Create Folder
            </button>
          </form>

          {/* Existing Folders */}
          <div>
            <h3 className="font-semibold mb-4">Your Folders ({folders.length})</h3>
            
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : folders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No folders yet. Create your first folder above!
              </div>
            ) : (
              <div className="space-y-3">
                {folders.map(folder => (
                  <div
                    key={folder.id}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    {editingFolder?.id === folder.id ? (
                      // Edit mode
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={editingFolder.name}
                            onChange={(e) => setEditingFolder({...editingFolder, name: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            maxLength={50}
                          />
                          <div className="flex">
                            <input
                              type="text"
                              value={editingFolder.hotkey}
                              onChange={(e) => setEditingFolder({...editingFolder, hotkey: e.target.value.toLowerCase().slice(0, 1)})}
                              placeholder="1-9, a-z"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              maxLength={1}
                            />
                            <button
                              type="button"
                              onClick={() => setEditingFolder({...editingFolder, hotkey: suggestHotkey(editingFolder.id)})}
                              className="px-3 py-2 bg-gray-200 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-300 text-sm"
                            >
                              Auto
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-6 gap-2 max-w-xs">
                          {colors.map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setEditingFolder({...editingFolder, color})}
                              className={`w-8 h-8 rounded border-2 ${editingFolder.color === color ? 'border-gray-800' : 'border-gray-300'} hover:scale-110 transition-transform`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => updateFolder(editingFolder)}
                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingFolder(null)}
                            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display mode
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: folder.color }}
                          />
                          <div>
                            <span className="font-medium">{folder.name}</span>
                            {folder.hotkey && (
                              <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded font-mono">
                                {folder.hotkey}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingFolder(folder)}
                            className="text-blue-500 hover:text-blue-700 px-2 py-1 rounded text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteFolder(folder.id, folder.name)}
                            className="text-red-500 hover:text-red-700 px-2 py-1 rounded text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hotkey Guide */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">How Hotkeys Work</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Press number keys (1-9) or letters (A-Z) in the lightbox to move images to folders</li>
              <li>• Images will automatically advance to the next after moving</li>
              <li>• Each folder can have only one hotkey</li>
              <li>• Hotkeys are case-insensitive</li>
            </ul>
          </div>
        </div>

        {/* Notification Modal */}
        <NotificationModal
          isOpen={showNotification}
          onClose={() => setShowNotification(false)}
          message={notificationMessage}
          type={notificationType}
        />
      </div>
    </div>
  );
};

export default FolderManager;
